import useSWR from "swr";
import axios from "../api/axios";
import type { AdminFeatures, AdminSession } from "../context/AdminSessionContext";
import { extractRoles } from "../utils/roleHelpers";
import { extractPermissions } from "../utils/permissionHelpers";

const SWR_KEY = "admin-session";

async function loadSession(): Promise<AdminSession> {
  const res = await axios.get("/auth/me");
  const featurePayload = res.data?.features ?? {};
  const features: AdminFeatures = {
    nameColors: Array.isArray(featurePayload?.nameColors) ? featurePayload.nameColors : [],
    avatarFrames: Array.isArray(featurePayload?.avatarFrames) ? featurePayload.avatarFrames : [],
    selectedColorId: featurePayload?.selectedColorId ?? featurePayload?.SelectedColorId ?? null,
    selectedFrameId: featurePayload?.selectedFrameId ?? featurePayload?.SelectedFrameId ?? null,
  };
  const featureFlags = normalizeFeatureFlags(res.data?.featureFlags ?? res.data?.FeatureFlags);

  return {
    id: res.data?.id ?? res.data?.Id ?? "",
    name: res.data?.name ?? res.data?.Name ?? "",
    roles: extractRoles(res.data),
    permissions: extractPermissions(res.data),
    avatarUrl: res.data?.avatarUrl ?? res.data?.AvatarUrl,
    features,
    featureFlags,
  };
}

async function fetchSession(): Promise<AdminSession | null> {
  try {
    return await loadSession();
  } catch (error) {
    const status = (error as { status?: number } | null)?.status;
    if (status === 403) {
      try {
        await axios.get("/auth/csrf/refresh");
        return await loadSession();
      } catch (retryError) {
        const retryStatus = (retryError as { status?: number } | null)?.status;
        if (retryStatus !== 401 && retryStatus !== 403) {
          console.warn("[admin] session retry failed", retryError);
        }
        return null;
      }
    }

    if (status !== 401 && status !== 403) {
      console.warn("[admin] session fetch failed", error);
    }
    return null;
  }
}

function normalizeFeatureFlags(payload: any): Record<string, boolean> | undefined {
  if (!payload) {
    return undefined;
  }

  if (Array.isArray(payload)) {
    return payload.reduce<Record<string, boolean>>((acc, item) => {
      const name = String(item?.name ?? item?.Name ?? "").trim().toLowerCase();
      if (!name) {
        return acc;
      }
      acc[name] = Boolean(item?.enabled ?? item?.Enabled ?? false);
      return acc;
    }, {});
  }

  if (typeof payload === "object") {
    return Object.entries(payload).reduce<Record<string, boolean>>((acc, [key, value]) => {
      const normalizedKey = String(key).trim().toLowerCase();
      if (!normalizedKey) {
        return acc;
      }
      acc[normalizedKey] = Boolean(value);
      return acc;
    }, {});
  }

  return undefined;
}

export default function useAdminSessionQuery() {
  return useSWR<AdminSession | null>(SWR_KEY, fetchSession, {
    revalidateOnFocus: true,
    shouldRetryOnError: false,
  });
}
